import React, { useEffect, useRef, useState } from "react";
import { Box, Input, Separator, Text, Button, Checkbox } from "@showitinc/showit-wc";
import ViewPanel from "../../blogsetup/ViewPanel";
import SetupButtonBar from "../../blogsetup/SetupButtonBar";
import useValidateWordpressLogin from "../../blogsetup/hooks/useValidateWordpressLogin";
import useSubmitSetupRequest from "../../blogsetup/hooks/useSubmitSetupRequest";
import { filterSetupData } from "../../blogsetup/utils";
import { invalidateUserBlogDomains } from "../../../hooks/query/useUserBlogDomains";
import UserActions from "../../../actions/UserActions";
import SiteActions from "../../../actions/SiteActions";
import Constants from "../Constants";
import { clearDraft } from "../draftStorage";
import styles from "./MigrationDetails.module.css";

function stripProtocol(url) {
  return url.replace(/^https?:\/\//, "");
}

function ensureProtocol(url) {
  return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
}

function canVerify(setupData) {
  const { blog_admin_url, blog_admin_user, blog_admin_pass } = setupData;
  return blog_admin_url && blog_admin_user && blog_admin_pass;
}

function MigrationDetails({ blogSetup, actions }) {
  const { setupData, userId } = blogSetup;
  const [showPassword, setShowPassword] = useState(false);
  const adminUrlRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => adminUrlRef.current?.focus(), 100);
    return () => clearTimeout(id);
  }, []);
  const { blog_admin_verified } = setupData;
  const [validateLogin, { isPending: isValidating }] = useValidateWordpressLogin();
  const [submitRequest, { isPending: isSubmitting, isError: isSubmitError, error: submitError }] = useSubmitSetupRequest();

  const submit = async () => {
    const setupRequest = {
      migrationRequired: true,
      setupData: {
        ...filterSetupData(setupData, Constants.BLOG_SETUP_FIELDS),
        blog_admin_url: ensureProtocol(setupData.blog_admin_url),
        configure_domain: Constants.CONFIGURE_DOMAIN_MIGRATE,
      },
    };
    await submitRequest(
      { userId, setupRequest },
      {
        onSuccess: (data) => {
          clearDraft();
          UserActions.updateConfiguration({
            blogId: data.blogDomain.wp_host_id,
            blogStatus: data.blogDomain.status,
          });
          SiteActions.domainConnectBlog(data.blogDomain);
          invalidateUserBlogDomains();
          actions.goToNextStep();
        },
      }
    );
  };

  const verify = async () => {
    const { blog_admin_url, blog_admin_user, blog_admin_pass } = setupData;
    const urlWithProtocol = ensureProtocol(blog_admin_url);
    actions.updateSetup({ blog_admin_url: urlWithProtocol });
    await validateLogin(
      { url: urlWithProtocol, username: blog_admin_user, password: blog_admin_pass },
      {
        onSuccess: (data) => {
          const { login } = data;
          if (login.isValid) {
            actions.updateSetup({ blog_admin_verified: Constants.BLOG_VERIFIED_LOGIN });
          } else {
            actions.updateSetup({ blog_admin_verified: Constants.BLOG_VERIFIED_INVALID });
          }
        },
      }
    );
  };

  const showWarning =
    blog_admin_verified === Constants.BLOG_VERIFIED_INVALID ||
    blog_admin_verified === Constants.BLOG_VERIFIED_USER;
  const credentialsVerified =
    blog_admin_verified === Constants.BLOG_VERIFIED_LOGIN ||
    blog_admin_verified === Constants.BLOG_VERIFIED_USER;

  let btnAction;
  let btnText = "Verify";

  if (credentialsVerified) {
    btnAction = isSubmitting ? undefined : submit;
    btnText = isSubmitting ? "Submitting..." : "Submit";
  } else if (canVerify(setupData)) {
    btnAction = verify;
  }

  if (isValidating) {
    btnAction = undefined;
    btnText = "Verifying";
  }

  return (
    <ViewPanel>
      {{
        content: (
          <>
            <Text variant="heading-2" variantStyle="strong">
              Migration Details
            </Text>
            <Separator />
            <Text>
              Ready to migrate your blog to Showit? We&apos;ll need some information about your
              current blog so we can move everything over.
            </Text>

            <Box my="3">
              <Text>What url do you use to log in to your blog?</Text>
              <Input
                ref={adminUrlRef}
                type="text"
                name="blog_admin_url"
                placeholder="example.com/wp-login.php"
                value={setupData.blog_admin_url}
                longLabel
                onSIInput={(e) =>
                  actions.updateSetup({
                    blog_admin_url: e.detail.value,
                    blog_admin_verified: undefined,
                  })
                }
                onSIBlur={(e) =>
                  actions.updateSetup({
                    blog_admin_url: stripProtocol((e.detail?.value || e.target.value).trim()),
                  })
                }
              />
            </Box>

            <Box mb="3">
              <Text>
                To access and migrate your content, please provide login information for your
                existing blog.
              </Text>
            </Box>
            <Input
              type="text"
              name="blog_admin_user"
              label="Username or Email"
              value={setupData.blog_admin_user}
              longLabel
              onSIInput={(e) =>
                actions.updateSetup({
                  blog_admin_user: e.detail.value,
                  blog_admin_verified: undefined,
                })
              }
            />
            <Input
              type={showPassword ? "text" : "password"}
              name="blog_admin_pass"
              label="Password"
              value={setupData.blog_admin_pass}
              longLabel
              onSIInput={(e) =>
                actions.updateSetup({
                  blog_admin_pass: e.detail.value,
                  blog_admin_verified: undefined,
                })
              }
            >
              <Button
                slot="suffix"
                variant="link"
                className={styles["show-password-button"]}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </Button>
            </Input>

            {blog_admin_verified === Constants.BLOG_VERIFIED_LOGIN && (
              <div className="has-success">
                <p className={`help-block ${styles["login-verified"]}`}>Login Verified</p>
              </div>
            )}

            {showWarning && (
              <>
                <Text className={styles["error-text"]}>
                  We could not log in to your blog with the info above.
                </Text>
                <Checkbox
                  type="checkbox"
                  name="current_blog_legit"
                  label="I can confirm that the credentials are correct."
                  checked={blog_admin_verified === Constants.BLOG_VERIFIED_USER}
                  onSIChange={(e) =>
                    actions.updateSetup({
                      blog_admin_verified: e.target.checked ? Constants.BLOG_VERIFIED_USER : undefined,
                    })
                  }
                />
              </>
            )}

            {isSubmitError && (
              <Box mt="2">
                <p className={styles["submit-error"]}>
                  {submitError?.message || "A problem occurred while submitting your request."}
                </p>
              </Box>
            )}
          </>
        ),
        actions: (
          // No back button: the domain has already been saved server-side by
          // the previous step's POST, so going back would just re-submit it.
          <SetupButtonBar
            onForward={btnAction}
            allowForward={btnAction !== undefined}
            allowBack={false}
            forwardBtnText={btnText}
          />
        ),
      }}
    </ViewPanel>
  );
}

export default MigrationDetails;
